// Version 1.0 done - Dec 22, 2025
// --------------------------------------
// Imports
// --------------------------------------
import * as THREE from "three";
import { MeshSurfaceSampler } from "three/examples/jsm/math/MeshSurfaceSampler.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";

import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
gsap.registerPlugin(SplitText);

// --------------------------------------
// Base Setup
// --------------------------------------
const canvas = document.querySelector("canvas.webgl");
const scene = new THREE.Scene();

const group = new THREE.Group();
group.position.x = -5;
// group.position.y = -3;
scene.add(group);

// --------------------------------------
// Sparkles System (CLEAN CORE)
// --------------------------------------
const MAX_PARTICLES = 40000;
const sparkles = [];

const sparklesGeometry = new THREE.BufferGeometry();
const positions = new Float32Array(MAX_PARTICLES * 3);
const sizes = new Float32Array(MAX_PARTICLES);
const colors = new Float32Array(MAX_PARTICLES * 3);

sparklesGeometry.setAttribute(
  "position",
  new THREE.BufferAttribute(positions, 3)
);
sparklesGeometry.setAttribute("size", new THREE.BufferAttribute(sizes, 1));
sparklesGeometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

const sparklesMaterial = new THREE.ShaderMaterial({
  uniforms: {
    pointTexture: {
      value: new THREE.TextureLoader().load("dotTexture.png"),
    },
  },
  vertexShader: `
    attribute float size;
    attribute vec3 color;
    varying vec3 vColor;

    void main() {
      vColor = color;
      vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
      gl_PointSize = size;
      gl_Position = projectionMatrix * mvPosition;
    }
  `,
  fragmentShader: `
    uniform sampler2D pointTexture;
    varying vec3 vColor;

    void main() {
      gl_FragColor = vec4(vColor, 1.0);
      if (texture2D(pointTexture, gl_PointCoord).g < 0.7) discard;
      gl_FragColor *= texture2D(pointTexture, gl_PointCoord);
    }
  `,
  depthTest: false,
  depthWrite: false,
  blending: THREE.AdditiveBlending,
});

const points = new THREE.Points(sparklesGeometry, sparklesMaterial);
group.add(points);

// --------------------------------------
// Themes
// --------------------------------------
const themes = [
  {
    name: "I Exist in Every Living Thing",
    content:
      "In every small life drifting through the dark, I see a piece of myself.",
    model: "dolphin.glb",
    colors: [0xfaad80, 0xff6767, 0xff3d68, 0xa73489],
    transform: {
      rotate: {
        x: 0,
        y: 0,
        z: 0,
      },
      scale: 1,
    },
  },
  {
    name: "We Are All Brief, Beautiful Moments",
    content:
      "Like these tiny beings, I appear, shine for a moment, and belong.",
    model: "goldfish.glb",
    colors: [0x80d8ff, 0x67b2ff, 0x3d7eff, 0x3450a7],
    transform: {
      rotate: {
        x: 0,
        y: 0,
        z: 0,
      },
      scale: 1,
    },
  },
  {
    name: "Held by a Vast and Gentle Universe",
    content: "Though I am tiny, the universe still makes room for my light.",
    model: "turtle.glb",
    colors: [0xffd700, 0xffc700, 0xffb200, 0xff9f00],
    transform: {
      rotate: {
        x: 0,
        y: 0,
        z: 0,
      },
      scale: 1,
    },
  },
];

let currentThemeIndex = 0;
let sampler = null;
let lines = [];
let currentTheme = null;

// --------------------------------------
// Sparkle Class
// --------------------------------------
class Sparkle extends THREE.Vector3 {
  setup(origin) {
    this.add(origin).multiplyScalar(4);
    this.dest = origin;
    this._size = Math.random() * 10 + 0.5;
    this.size = 1.5;
    this.scaleSpeed = Math.random() * 0.03 + 0.03;
  }
  update() {
    this.lerp(this.dest, 0.08);
    if (this.size < this._size) this.size += this.scaleSpeed;
  }
}

// --------------------------------------
// Theme Handling
// --------------------------------------
const loader = new GLTFLoader();

let themeTimeline = null;
let titleSplit = null;
let contentSplit = null;

function animateThemeText(titleText, contentText) {
  const titleEl = document.getElementById("theme-title");
  const contentEl = document.getElementById("theme-content");

  // 清理旧动画
  if (themeTimeline) {
    themeTimeline.kill();
    themeTimeline = null;
  }

  // 清理旧 SplitText
  if (titleSplit) {
    titleSplit.revert();
    titleSplit = null;
  }
  if (contentSplit) {
    contentSplit.revert();
    contentSplit = null;
  }

  // 更新文字
  titleEl.innerText = titleText;
  contentEl.innerText = contentText;

  // 拆分标题
  titleSplit = SplitText.create(titleEl, {
    type: "words",
    autoSplit: true,
    mask: "words",
    wordsClass: "word",
  });

  // 拆分内容
  contentSplit = SplitText.create(contentEl, {
    type: "words",
    autoSplit: true,
    mask: "words",
    wordsClass: "word",
  });

  gsap.set([titleEl, contentEl], { visibility: "visible" });

  // timeline 动画（标题先，内容后）
  themeTimeline = gsap.timeline();
  themeTimeline
    .from(titleSplit.words, {
      yPercent: 50,
      opacity: 0,
      duration: 1,
      ease: "power2.out",
      stagger: 0.05,
    })
    .from(
      contentSplit.words,
      {
        yPercent: 50,
        opacity: 0,
        duration: 1,
        ease: "power2.out",
        stagger: 0.03,
      },
      "-=0.2" // 可选：标题和内容有少量重叠，让过渡更自然
    );
}

function switchTheme() {
  currentTheme = themes[currentThemeIndex];
  animateThemeText(currentTheme.name, currentTheme.content);

  resetSparkles();
  loadModel(currentTheme.model, currentTheme.colors, currentTheme.transform);
  group.rotation.y = 0;

  currentThemeIndex = (currentThemeIndex + 1) % themes.length;
}

function resetSparkles() {
  sparkles.length = 0;
  lines.length = 0;
}

function loadModel(path, colorSet, transform) {
  loader.load(path, (gltf) => {
    const model = gltf.scene;
    const mesh = model.getObjectByProperty("type", "Mesh");

    // --------------------
    // 1️⃣ 应用旋转（geometry 级别）
    // --------------------
    if (transform?.rotate) {
      const r = transform.rotate;
      if (r.x) mesh.geometry.rotateX(THREE.MathUtils.degToRad(r.x));
      if (r.y) mesh.geometry.rotateY(THREE.MathUtils.degToRad(r.y));
      if (r.z) mesh.geometry.rotateZ(THREE.MathUtils.degToRad(r.z));
    }

    // --------------------
    // 2️⃣ 应用缩放
    // --------------------
    if (transform?.scale) {
      mesh.geometry.scale(transform.scale, transform.scale, transform.scale);
    }

    // 原本就有的
    // mesh.geometry.translate(0, -2, 0);

    sampler = new MeshSurfaceSampler(mesh).build();

    const themeColors = colorSet.map((c) =>
      new THREE.Color(c).multiplyScalar(0.5)
    );

    for (let i = 0; i < 6; i++) {
      const p = new THREE.Vector3();
      sampler.sample(p);
      lines.push({
        previous: p.clone(),
        color: themeColors[i % themeColors.length],
      });
    }
  });
}

switchTheme();
setInterval(switchTheme, 12000);

// --------------------------------------
// Generate Sparkles
// --------------------------------------
function generateSparkles() {
  if (!sampler || sparkles.length >= MAX_PARTICLES) return;

  lines.forEach((line) => {
    for (let i = 0; i < 3; i++) {
      if (sparkles.length >= MAX_PARTICLES) return;

      const p = new THREE.Vector3();
      let ok = false;

      while (!ok) {
        sampler.sample(p);
        if (p.distanceTo(line.previous) < 2) {
          line.previous.copy(p);

          const s = new Sparkle();
          s.setup(p.clone());
          sparkles.push(s);

          const idx = sparkles.length - 1;
          colors[idx * 3] = line.color.r;
          colors[idx * 3 + 1] = line.color.g;
          colors[idx * 3 + 2] = line.color.b;

          ok = true;
        }
      }
    }
  });
}

// --------------------------------------
// Camera / Renderer
// --------------------------------------
const windowSizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

const camera = new THREE.PerspectiveCamera(
  75,
  windowSizes.width / windowSizes.height,
  0.1,
  1000
);
camera.position.set(0, 10, 10);
camera.lookAt(0, 0, 0);
scene.add(camera);

const renderer = new THREE.WebGLRenderer({ canvas });
renderer.setSize(windowSizes.width, windowSizes.height);
let pixelRatio = Math.min(window.devicePixelRatio, 2);
renderer.setPixelRatio(pixelRatio);

let galaxyColors = [
  new THREE.Color("#f9fbf2").multiplyScalar(0.2),
  new THREE.Color("#ffede1").multiplyScalar(0.1),
  new THREE.Color("#05c7f2").multiplyScalar(0.2),
  new THREE.Color("#0597f2").multiplyScalar(0.2),
  new THREE.Color("#0476d9").multiplyScalar(0.2),
];

class Star {
  setup(color) {
    this.r = Math.random() * 20 + 8;

    // const minR = 8;
    // const maxR = 28;
    // this.r = Math.cbrt(Math.random()) * (maxR - minR) + minR;

    this.phi = Math.random() * Math.PI;
    this.theta = Math.random() * Math.PI * 2;
    // [-0.5 0.5] * 0.0007
    this.v = new THREE.Vector2().random().subScalar(0.5).multiplyScalar(0.0007);

    this.x = this.r * Math.sin(this.phi) * Math.sin(this.theta);
    this.y = this.r * Math.cos(this.phi);
    this.z = this.r * Math.sin(this.phi) * Math.cos(this.theta);

    this.size = Math.random() * 20 + 0.5 * pixelRatio;
    this.color = color;
  }
  update() {
    this.phi += this.v.x;
    this.theta += this.v.y;
    this.x = this.r * Math.sin(this.phi) * Math.sin(this.theta);
    this.y = this.r * Math.cos(this.phi);
    this.z = this.r * Math.sin(this.phi) * Math.cos(this.theta);
  }
}

// --------------------------------------
// Create stars (初始化一次)
// --------------------------------------
const stars = [];
const starPositions = new Float32Array(1500 * 3);
const starColors = new Float32Array(1500 * 3);
const starSizes = new Float32Array(1500);

for (let i = 0; i < 1500; i++) {
  const star = new Star();
  const color = galaxyColors[Math.floor(Math.random() * galaxyColors.length)];
  star.setup(color);
  stars.push(star);

  // 初始化 attribute 数组
  starPositions[i * 3] = star.x;
  starPositions[i * 3 + 1] = star.y;
  starPositions[i * 3 + 2] = star.z;

  starColors[i * 3] = star.color.r;
  starColors[i * 3 + 1] = star.color.g;
  starColors[i * 3 + 2] = star.color.b;

  starSizes[i] = star.size;
}

const starsGeometry = new THREE.BufferGeometry();
starsGeometry.setAttribute(
  "position",
  new THREE.BufferAttribute(starPositions, 3)
);
starsGeometry.setAttribute("color", new THREE.BufferAttribute(starColors, 3));
starsGeometry.setAttribute("size", new THREE.BufferAttribute(starSizes, 1));

const galaxyPoints = new THREE.Points(starsGeometry, sparklesMaterial);
scene.add(galaxyPoints);

// --------------------------------------
// Animate stars (每帧只更新 array)
// --------------------------------------
function updateStars() {
  stars.forEach((s, i) => {
    s.update();
    starPositions[i * 3] = s.x;
    starPositions[i * 3 + 1] = s.y;
    starPositions[i * 3 + 2] = s.z;
  });

  starsGeometry.attributes.position.needsUpdate = true;
}

// --------------------------------------
// Animate
// --------------------------------------
function tick() {
  group.rotation.y += 0.001;

  generateSparkles();

  sparkles.forEach((s, i) => {
    s.update();
    positions[i * 3] = s.x;
    positions[i * 3 + 1] = s.y;
    positions[i * 3 + 2] = s.z;
    sizes[i] = s.size;
  });

  sparklesGeometry.attributes.position.needsUpdate = true;
  sparklesGeometry.attributes.size.needsUpdate = true;
  sparklesGeometry.attributes.color.needsUpdate = true;
  sparklesGeometry.setDrawRange(0, sparkles.length);

  // ⭐ 更新星星
  updateStars();

  renderer.render(scene, camera);
  requestAnimationFrame(tick);
}

tick();
